import api from './api';
import { LoginCredentials, AuthResponse, User } from '../types';
import { API_ENDPOINTS, STORAGE_KEYS } from '../constants';

const authService = {
  /**
   * Login user with phone and PIN
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post<{ success: boolean; message: string; data: AuthResponse }>(API_ENDPOINTS.AUTH.LOGIN, credentials);
    const { token, refreshToken, user } = response.data.data;
    
    // Store tokens and user data
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
    
    return { token, refreshToken, user };
  },
  
  /**
   * Refresh authentication token
   */
  refreshToken: async (): Promise<{ token: string; refreshToken: string }> => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await api.post<{ success: boolean; message: string; data: { token: string; refreshToken: string } }>(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
      refreshToken,
    });
    
    const { token, refreshToken: newRefreshToken } = response.data.data;
    
    // Update stored tokens
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
    
    return { token, refreshToken: newRefreshToken };
  },
  
  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    try {
      await api.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      
      // Redirect to login page
      window.location.href = '/login';
    }
  },
  
  /**
   * Get current authenticated user
   */
  getCurrentUser: (): User | null => {
    const userJson = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
    if (!userJson || userJson === 'undefined' || userJson === 'null') {
      // Clear invalid data
      localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      return null;
    }
    
    try {
      return JSON.parse(userJson);
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      return null;
    }
  },
  
  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const user = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
    return !!(token && user);
  },
  
  /**
   * Get authentication token
   */
  getToken: (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },
  
  /**
   * Get user profile from API
   */
  getProfile: async (): Promise<User> => {
    const response = await api.get<{ success: boolean; message: string; data: User }>(API_ENDPOINTS.AUTH.PROFILE);
    return response.data.data;
  },
  
  /**
   * Verify token validity
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      await api.get(API_ENDPOINTS.AUTH.VERIFY);
      return true;
    } catch (error) {
      return false;
    }
  },
};

export default authService;
